import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiGlobe } from 'react-icons/fi';
import { persistLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';

// Text badge shown for each supported language — kept short so the button
// stays compact in both the (dark) storefront Header and the (light)
// AdminLayout top bar that consumes this component.
const LANGUAGE_BADGES: Record<SupportedLanguage, string> = {
  en: 'EN',
  zh: '中',
};

// Full, native-script name for each supported language, shown in the
// dropdown list. Language names are conventionally shown in their own
// language regardless of the current UI language, so these are not run
// through i18next — same rationale as LANGUAGE_BADGES above.
const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  zh: '中文',
};

const ToggleContainer = styled.div`
  position: relative;
`;

const ToggleButton = styled.button<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: auto;
  height: 44px;
  padding: 0 0.5rem;
  color: ${props => props.$color || 'inherit'};
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: var(--primary-color);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const LanguageBadge = styled.span<{ $hideOnMobile?: boolean }>`
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;

  ${props => props.$hideOnMobile && `
    @media (max-width: 480px) {
      display: none;
    }
  `}
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%; /* No gap to prevent mouse leaving */
  right: 0;
  background-color: var(--white);
  border: 1px solid var(--gray-light);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  margin-top: 4px; /* Visual spacing */
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: opacity 0.2s, visibility 0.2s, transform 0.2s;
  z-index: 1001;
  overflow: hidden;

  /* Invisible bridge to maintain hover */
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    right: 0;
    height: 4px;
  }
`;

const DropdownOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  color: var(--text-color);
  background: ${props => props.$active ? 'var(--gray-light)' : 'transparent'};
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  font-family: inherit;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--gray-light);
  }
`;

interface LanguageToggleProps {
  // Optional override for the button's resting-state color. Defaults to
  // `inherit`, which works fine against AdminLayout's light TopBar (dark
  // text is legible there without help). The dark storefront Header must
  // pass its icon color explicitly (e.g. '#FAF8F3') — `inherit` there would
  // resolve to the header's own near-black text color, making the toggle
  // blend into the near-black header background until hovered/focused,
  // which is invisible to keyboard- and touch-only users who never trigger
  // :hover.
  color?: string;
  // localStorage key this toggle persists its language choice under. Every
  // call site must pass this explicitly (Header uses
  // CUSTOMER_LANGUAGE_STORAGE_KEY, AdminLayout uses
  // ADMIN_LANGUAGE_STORAGE_KEY) so choosing a language on one surface never
  // overwrites the other surface's stored preference.
  storageKey: string;
  // Optional: hide the text badge (EN/中) at narrow widths (<=480px),
  // leaving only the globe icon. Header.tsx's Actions row is tight on
  // space at small phone widths and needs this to keep the hamburger
  // MenuButton reachable; AdminLayout's TopBar has more room and does not
  // pass this, so its badge keeps showing at all widths.
  hideBadgeOnMobile?: boolean;
}

// Reusable language switcher, consumed by both Header.tsx (customer
// storefront) and AdminLayout.tsx (admin console) so both surfaces get
// identical dropdown behavior. Hovering (or clicking, for touch/keyboard
// users) the globe button opens a dropdown listing every language in
// SUPPORTED_LANGUAGES; clicking an option switches i18next's active
// language via i18next.changeLanguage. This only updates React
// state/localStorage, it does not navigate or reload the page.
const LanguageToggle: React.FC<LanguageToggleProps> = ({ color, storageKey, hideBadgeOnMobile }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && event.target && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    // Touch browsers fire a synthetic mouseenter immediately before click on
    // the first tap of an element (there is no real hover on touch). Without
    // this guard that synthetic mouseenter would open the menu and the same
    // tap's click would then immediately toggle it shut again, making the
    // dropdown untappable on mobile. `(hover: hover)` only matches on
    // devices with a real pointing device, so touch taps fall through to
    // the trigger's onClick below instead.
    if (!window.matchMedia?.('(hover: hover)').matches) {
      return;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (!window.matchMedia?.('(hover: hover)').matches) {
      return;
    }
    const relatedTarget = e.relatedTarget;

    if (!containerRef.current ||
        !relatedTarget ||
        !(relatedTarget instanceof Element) ||
        !containerRef.current.contains(relatedTarget)) {
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 300);
    }
  };

  const handleSelect = (language: SupportedLanguage) => {
    if (language !== currentLanguage) {
      i18n.changeLanguage(language);
      persistLanguage(storageKey, language);
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <ToggleContainer
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
    >
      <ToggleButton
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('common.language')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        $color={color}
      >
        <FiGlobe />
        <LanguageBadge $hideOnMobile={hideBadgeOnMobile}>{LANGUAGE_BADGES[currentLanguage]}</LanguageBadge>
      </ToggleButton>

      <DropdownMenu $isOpen={isOpen} role="menu">
        {SUPPORTED_LANGUAGES.map(language => (
          <DropdownOption
            key={language}
            type="button"
            role="menuitemradio"
            aria-checked={language === currentLanguage}
            $active={language === currentLanguage}
            onClick={() => handleSelect(language)}
          >
            <span>{LANGUAGE_NAMES[language]}</span>
            <LanguageBadge>{LANGUAGE_BADGES[language]}</LanguageBadge>
          </DropdownOption>
        ))}
      </DropdownMenu>
    </ToggleContainer>
  );
};

export default LanguageToggle;
